import { useState, useEffect, useCallback } from 'react'
import clsx from 'clsx'

// FDI (Fédération Dentaire Internationale) Numbering System
// Quadrant 1 (11-18): Upper Right (Permanent)
// Quadrant 2 (21-28): Upper Left (Permanent)
// Quadrant 3 (31-38): Lower Left (Permanent)
// Quadrant 4 (41-48): Lower Right (Permanent)

interface ToothChartProps {
  selectedTeeth: string[]
  onSelectionChange: (teeth: string[]) => void
}

interface ToothData {
  id: string      // FDI number (also used as id)
  fdi: string     // FDI number for display
  name: string
  type: 'molar' | 'premolar' | 'canine' | 'incisor'
  position: number
}

// ─── Tooth data (anatomically correct ordering) ──────────────────────────────
// FDI Numbering System layout from viewer's perspective:
// Q1 Upper Right: 18,17,16,15,14,13,12,11 | Q2 Upper Left: 21,22,23,24,25,26,27,28
// Q4 Lower Right: 48,47,46,45,44,43,42,41 | Q3 Lower Left: 31,32,33,34,35,36,37,38

const UPPER_RIGHT: ToothData[] = [
  { id: '18', fdi: '18', name: 'Third Molar',      type: 'molar',    position: 8 },
  { id: '17', fdi: '17', name: 'Second Molar',     type: 'molar',    position: 7 },
  { id: '16', fdi: '16', name: 'First Molar',      type: 'molar',    position: 6 },
  { id: '15', fdi: '15', name: 'Second Premolar',  type: 'premolar', position: 5 },
  { id: '14', fdi: '14', name: 'First Premolar',   type: 'premolar', position: 4 },
  { id: '13', fdi: '13', name: 'Canine',           type: 'canine',   position: 3 },
  { id: '12', fdi: '12', name: 'Lateral Incisor',  type: 'incisor',  position: 2 },
  { id: '11', fdi: '11', name: 'Central Incisor',  type: 'incisor',  position: 1 },
]

const UPPER_LEFT: ToothData[] = [
  { id: '21', fdi: '21', name: 'Central Incisor',  type: 'incisor',  position: 1 },
  { id: '22', fdi: '22', name: 'Lateral Incisor',  type: 'incisor',  position: 2 },
  { id: '23', fdi: '23', name: 'Canine',           type: 'canine',   position: 3 },
  { id: '24', fdi: '24', name: 'First Premolar',   type: 'premolar', position: 4 },
  { id: '25', fdi: '25', name: 'Second Premolar',  type: 'premolar', position: 5 },
  { id: '26', fdi: '26', name: 'First Molar',      type: 'molar',    position: 6 },
  { id: '27', fdi: '27', name: 'Second Molar',     type: 'molar',    position: 7 },
  { id: '28', fdi: '28', name: 'Third Molar',      type: 'molar',    position: 8 },
]

const LOWER_RIGHT: ToothData[] = [
  { id: '48', fdi: '48', name: 'Third Molar',      type: 'molar',    position: 8 },
  { id: '47', fdi: '47', name: 'Second Molar',     type: 'molar',    position: 7 },
  { id: '46', fdi: '46', name: 'First Molar',      type: 'molar',    position: 6 },
  { id: '45', fdi: '45', name: 'Second Premolar',  type: 'premolar', position: 5 },
  { id: '44', fdi: '44', name: 'First Premolar',   type: 'premolar', position: 4 },
  { id: '43', fdi: '43', name: 'Canine',           type: 'canine',   position: 3 },
  { id: '42', fdi: '42', name: 'Lateral Incisor',  type: 'incisor',  position: 2 },
  { id: '41', fdi: '41', name: 'Central Incisor',  type: 'incisor',  position: 1 },
]

const LOWER_LEFT: ToothData[] = [
  { id: '31', fdi: '31', name: 'Central Incisor',  type: 'incisor',  position: 1 },
  { id: '32', fdi: '32', name: 'Lateral Incisor',  type: 'incisor',  position: 2 },
  { id: '33', fdi: '33', name: 'Canine',           type: 'canine',   position: 3 },
  { id: '34', fdi: '34', name: 'First Premolar',   type: 'premolar', position: 4 },
  { id: '35', fdi: '35', name: 'Second Premolar',  type: 'premolar', position: 5 },
  { id: '36', fdi: '36', name: 'First Molar',      type: 'molar',    position: 6 },
  { id: '37', fdi: '37', name: 'Second Molar',     type: 'molar',    position: 7 },
  { id: '38', fdi: '38', name: 'Third Molar',      type: 'molar',    position: 8 },
]

const ALL_TEETH = [...UPPER_RIGHT, ...UPPER_LEFT, ...LOWER_RIGHT, ...LOWER_LEFT]

const findToothByFDI = (fdiNumber: string): ToothData | undefined =>
  ALL_TEETH.find(t => t.fdi === fdiNumber)

// ─── Realistic tooth SVG paths ──────────────────────────────────────────────
// Designed to match the reference dental chart with anatomically distinct shapes.
// Upper teeth: roots point UP, crown at bottom. Lower teeth: roots point DOWN, crown at top.
// All paths designed for viewBox="0 0 40 90" (upper) or "0 0 40 90" (lower).

// Returns { crown, roots, cusp } for UPPER teeth (roots pointing upward, crown at bottom)
function getUpperToothShape(type: ToothData['type'], isThirdMolar: boolean) {
  if (type === 'molar') {
    if (isThirdMolar) {
      // Smaller, slightly irregular third molar
      return {
        crown: 'M8,48 C6,48 4,52 4,56 C4,60 5,66 8,70 C10,73 14,75 17,75 L23,75 C26,75 30,73 32,70 C35,66 36,60 36,56 C36,52 34,48 32,48 C29,47 27,46 25,47 C23,48 22,47 20,46 C18,47 16,47 14,48 Z',
        roots: [
          'M12,48 C12,44 11,38 10,32 C9,26 10,20 12,16',
          'M20,46 C20,42 20,36 20,30 C20,24 20,18 20,14',
          'M28,48 C28,44 30,38 31,32 C32,26 31,20 29,16',
        ],
        cusp: 'M10,52 L14,48 L18,52 L22,47 L26,52 L30,48 L34,53',
      }
    }
    // Standard molar: wide crown with 4-5 cusps, 3 roots (2 buccal + 1 palatal)
    return {
      crown: 'M6,46 C3,46 1,50 1,55 C1,60 2,67 6,72 C8,75 13,78 17,78 L23,78 C27,78 32,75 34,72 C38,67 39,60 39,55 C39,50 37,46 34,46 C31,45 28,44 26,45 C24,46 22,45 20,44 C18,45 15,45 12,46 Z',
      roots: [
        'M11,46 C11,42 10,36 9,30 C8,24 9,16 11,10 C12,7 13,5 14,4',
        'M20,44 C20,40 20,34 20,28 C20,22 20,14 20,8 C20,5 20,3 20,2',
        'M29,46 C29,42 31,36 32,30 C33,24 32,16 30,10 C29,7 28,5 27,4',
      ],
      cusp: 'M6,52 L11,46 L16,52 L20,45 L24,52 L29,46 L34,52',
    }
  }

  if (type === 'premolar') {
    // Premolar: narrower than molar, 2 cusps, 1-2 roots
    return {
      crown: 'M10,48 C7,48 5,52 5,56 C5,62 7,68 10,72 C12,75 16,78 20,78 C24,78 28,75 30,72 C33,68 35,62 35,56 C35,52 33,48 30,48 C27,47 24,46 22,47 C19,46 16,47 14,48 Z',
      roots: [
        'M16,48 C16,44 15,38 14,30 C13,22 15,14 17,8 C18,5 19,3 20,2',
        'M24,48 C24,44 25,38 26,30 C27,22 25,14 23,8 C22,5 21,3 20,2',
      ],
      cusp: 'M12,52 L18,47 L22,52 L28,47 L32,52',
    }
  }

  if (type === 'canine') {
    // Canine: single prominent pointed cusp, longest root
    return {
      crown: 'M12,52 C9,52 7,56 7,60 C7,65 9,70 12,74 C14,77 17,79 20,79 C23,79 26,77 28,74 C31,70 33,65 33,60 C33,56 31,52 28,52 C26,51 23,50 21,52 C19,50 16,51 14,52 Z',
      roots: [
        'M20,50 C20,46 20,40 20,32 C20,24 20,16 20,8 C20,5 20,3 20,1',
      ],
      cusp: 'M14,56 L20,49 L26,56',
    }
  }

  // Incisor: flat broad crown (shovel-shaped), single root
  return {
    crown: 'M12,50 C9,50 7,54 7,58 C7,63 8,68 11,72 C13,75 16,78 20,78 C24,78 27,75 29,72 C32,68 33,63 33,58 C33,54 31,50 28,50 Z',
    roots: [
      'M20,50 C20,46 20,40 19,32 C19,24 20,16 20,8 C20,5 20,3 20,2',
    ],
    cusp: 'M12,54 L20,50 L28,54',
  }
}

// Returns paths for LOWER teeth (roots pointing downward, crown at top)
function getLowerToothShape(type: ToothData['type'], isThirdMolar: boolean) {
  if (type === 'molar') {
    if (isThirdMolar) {
      return {
        crown: 'M8,42 C6,42 4,38 4,34 C4,30 5,24 8,20 C10,17 14,15 17,15 L23,15 C26,15 30,17 32,20 C35,24 36,30 36,34 C36,38 34,42 32,42 C29,43 27,44 25,43 C23,42 22,43 20,44 C18,43 16,43 14,42 Z',
        roots: [
          'M12,42 C12,46 11,52 10,58 C9,64 10,70 12,74',
          'M28,42 C28,46 30,52 31,58 C32,64 31,70 29,74',
        ],
        cusp: 'M10,38 L14,42 L18,38 L22,43 L26,38 L30,42 L34,37',
      }
    }
    return {
      crown: 'M6,44 C3,44 1,40 1,35 C1,30 2,23 6,18 C8,15 13,12 17,12 L23,12 C27,12 32,15 34,18 C38,23 39,30 39,35 C39,40 37,44 34,44 C31,45 28,46 26,45 C24,44 22,45 20,46 C18,45 15,45 12,44 Z',
      roots: [
        'M14,44 C14,48 13,54 12,60 C11,66 12,74 14,80 C15,83 16,85 17,86',
        'M26,44 C26,48 27,54 28,60 C29,66 28,74 26,80 C25,83 24,85 23,86',
      ],
      cusp: 'M6,38 L11,44 L16,38 L20,45 L24,38 L29,44 L34,38',
    }
  }

  if (type === 'premolar') {
    return {
      crown: 'M10,42 C7,42 5,38 5,34 C5,28 7,22 10,18 C12,15 16,12 20,12 C24,12 28,15 30,18 C33,22 35,28 35,34 C35,38 33,42 30,42 C27,43 24,44 22,43 C19,44 16,43 14,42 Z',
      roots: [
        'M20,42 C20,46 20,52 20,58 C20,64 20,72 20,80 C20,84 20,87 20,88',
      ],
      cusp: 'M12,38 L18,43 L22,38 L28,43 L32,38',
    }
  }

  if (type === 'canine') {
    return {
      crown: 'M12,38 C9,38 7,34 7,30 C7,25 9,20 12,16 C14,13 17,11 20,11 C23,11 26,13 28,16 C31,20 33,25 33,30 C33,34 31,38 28,38 C26,39 23,40 21,38 C19,40 16,39 14,38 Z',
      roots: [
        'M20,40 C20,44 20,50 20,58 C20,66 20,74 20,82 C20,85 20,87 20,89',
      ],
      cusp: 'M14,34 L20,39 L26,34',
    }
  }

  // Incisor
  return {
    crown: 'M12,40 C9,40 7,36 7,32 C7,27 8,22 11,18 C13,15 16,12 20,12 C24,12 27,15 29,18 C32,22 33,27 33,32 C33,36 31,40 28,40 Z',
    roots: [
      'M20,40 C20,44 20,50 19,58 C19,66 20,74 20,82 C20,85 20,87 20,88',
    ],
    cusp: 'M12,36 L20,40 L28,36',
  }
}

// ─── SVG Defs ────────────────────────────────────────────────────────────────

function ChartDefs() {
  return (
    <defs>
      <linearGradient id="toothFill" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FAFAFA" />
        <stop offset="100%" stopColor="#E8E8E8" />
      </linearGradient>
      <linearGradient id="selectedFill" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#A5F3FC" />
        <stop offset="50%" stopColor="#67E8F9" />
        <stop offset="100%" stopColor="#22D3EE" />
      </linearGradient>
      <filter id="toothShadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.12" />
      </filter>
      <filter id="selectedGlow" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#06B6D4" floodOpacity="0.35" />
      </filter>
    </defs>
  )
}

// ─── Single Tooth Component ──────────────────────────────────────────────────

interface ToothCellProps {
  tooth: ToothData
  isSelected: boolean
  onSelect: () => void
  displayNumber: string
  isUpper: boolean
  size: number
  compact?: boolean
}

function ToothCell({ tooth, isSelected, onSelect, displayNumber, isUpper, size, compact = false }: ToothCellProps) {
  const isThirdMolar = tooth.position === 8
  const paths = isUpper
    ? getUpperToothShape(tooth.type, isThirdMolar)
    : getLowerToothShape(tooth.type, isThirdMolar)

  // Scale the viewBox: upper teeth use 0 0 40 90, lower teeth use 0 0 40 90
  const vw = 40
  const vh = 90
  const svgW = size
  const svgH = size * (vh / vw) * (compact ? 0.85 : 0.95)

  const strokeColor = isSelected ? '#0891B2' : '#9CA3AF'
  const fillColor = isSelected ? 'url(#selectedFill)' : 'url(#toothFill)'
  const rootColor = isSelected ? '#0D9488' : '#D1D5DB'

  const numberFontSize = compact ? Math.max(8, size * 0.28) : Math.max(9, size * 0.3)

  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSelect() }}
      className={clsx(
        'relative flex flex-col items-center justify-center focus:outline-none touch-manipulation select-none',
        'transition-all duration-150 ease-out',
        'hover:scale-110 active:scale-95',
        'rounded-md',
        isSelected && 'z-10'
      )}
      style={{
        width: compact ? size + 2 : size + 6,
        padding: compact ? '1px 0' : '2px 1px',
      }}
      title={`${tooth.name} (${displayNumber})`}
    >
      {/* Number ABOVE for upper teeth */}
      {isUpper && (
        <span
          className={clsx(
            'font-mono font-bold leading-none text-center transition-colors',
            isSelected ? 'text-cyan-600' : 'text-gray-500 dark:text-gray-400',
          )}
          style={{ fontSize: numberFontSize, marginBottom: 1 }}
        >
          {displayNumber}
        </span>
      )}

      <svg
        width={svgW}
        height={svgH}
        viewBox={`0 0 ${vw} ${vh}`}
        className="overflow-visible"
        style={{
          filter: isSelected ? 'url(#selectedGlow)' : 'url(#toothShadow)',
        }}
      >
        <ChartDefs />

        {/* Roots */}
        {paths.roots.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={rootColor}
            strokeWidth={2.5}
            strokeLinecap="round"
          />
        ))}

        {/* Crown body */}
        <path
          d={paths.crown}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={isSelected ? 2 : 1.5}
          strokeLinejoin="round"
        />

        {/* Cusp line for anatomical detail */}
        <path
          d={paths.cusp}
          fill="none"
          stroke={isSelected ? '#0891B2' : '#C0C0C0'}
          strokeWidth={1}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.6}
        />

        {/* Selection indicator */}
        {isSelected && (
          <g transform={isUpper ? 'translate(20, 62)' : 'translate(20, 28)'}>
            <circle r="6" fill="#0891B2" stroke="white" strokeWidth="1.5" />
            <path
              d="M-2.5 0 L-0.5 2.5 L3 -2"
              fill="none"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        )}
      </svg>

      {/* Number BELOW for lower teeth */}
      {!isUpper && (
        <span
          className={clsx(
            'font-mono font-bold leading-none text-center transition-colors',
            isSelected ? 'text-cyan-600' : 'text-gray-500 dark:text-gray-400',
          )}
          style={{ fontSize: numberFontSize, marginTop: 1 }}
        >
          {displayNumber}
        </span>
      )}
    </button>
  )
}

// ─── Quadrant types ──────────────────────────────────────────────────────────

type ActiveQuadrant = 'UR' | 'UL' | 'LL' | 'LR'

const QUADRANT_META: Record<ActiveQuadrant, { label: string; qNum: string; teeth: ToothData[]; isUpper: boolean }> = {
  UR: { label: 'Upper Right', qNum: 'Q1', teeth: UPPER_RIGHT, isUpper: true },
  UL: { label: 'Upper Left',  qNum: 'Q2', teeth: UPPER_LEFT,  isUpper: true },
  LR: { label: 'Lower Right', qNum: 'Q4', teeth: LOWER_RIGHT, isUpper: false },
  LL: { label: 'Lower Left',  qNum: 'Q3', teeth: LOWER_LEFT,  isUpper: false },
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function ToothChart({
  selectedTeeth, onSelectionChange,
}: ToothChartProps) {
  // Selection stored as FDI numbers (strings)
  const [selection, setSelection] = useState<Set<string>>(() => new Set(selectedTeeth))
  const [activeQuadrant, setActiveQuadrant] = useState<ActiveQuadrant>('UR')
  const [isMobile, setIsMobile] = useState(false)
  const [toothSize, setToothSize] = useState(32)

  // Responsive sizing
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth
      setIsMobile(w < 640)
      if (w >= 1024) {
        setToothSize(Math.min(38, Math.floor((w - 200) / 20)))
      } else if (w >= 768) {
        setToothSize(Math.min(34, Math.floor((w - 120) / 18)))
      } else {
        const mobileAvailableWidth = Math.max(220, w - 104)
        const estimatedSize = Math.floor((mobileAvailableWidth - 12) / 8)
        setToothSize(Math.max(22, Math.min(28, estimatedSize)))
      }
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Sync external selection changes
  useEffect(() => {
    const newSet = new Set(selectedTeeth)
    const currentStr = [...selection].sort().join(',')
    const newStr = [...newSet].sort().join(',')
    if (currentStr !== newStr) setSelection(newSet)
  }, [selectedTeeth])

  const isSelected = useCallback((tooth: ToothData) => selection.has(tooth.fdi), [selection])

  const toggleTooth = useCallback((tooth: ToothData) => {
    const newSelection = new Set(selection)
    if (newSelection.has(tooth.fdi)) {
      newSelection.delete(tooth.fdi)
    } else {
      newSelection.add(tooth.fdi)
    }
    setSelection(newSelection)
    onSelectionChange([...newSelection])
  }, [selection, onSelectionChange])

  const selectAll = () => {
    const allTeeth = ALL_TEETH.map(t => t.fdi)
    setSelection(new Set(allTeeth))
    onSelectionChange(allTeeth)
  }

  const clearAll = () => {
    setSelection(new Set())
    onSelectionChange([])
  }

  const selectQuadrant = (q: ActiveQuadrant) => {
    const quadrantTeeth = QUADRANT_META[q].teeth.map(t => t.fdi)
    const allSelected = quadrantTeeth.every(u => selection.has(u))
    const newSelection = new Set(selection)

    if (allSelected) {
      // Deselect all in quadrant
      quadrantTeeth.forEach(u => newSelection.delete(u))
    } else {
      // Select all in quadrant
      quadrantTeeth.forEach(u => newSelection.add(u))
    }

    setSelection(newSelection)
    onSelectionChange([...newSelection])
  }

  // Sort FDI numbers for display (by quadrant, then by position)
  const sortFDI = (a: string, b: string) => {
    const numA = parseInt(a)
    const numB = parseInt(b)
    const quadA = Math.floor(numA / 10)
    const quadB = Math.floor(numB / 10)
    if (quadA !== quadB) return quadA - quadB
    return numA - numB
  }

  const displaySelection = [...selection].sort(sortFDI)

  const qCount = (q: ActiveQuadrant) => QUADRANT_META[q].teeth.filter(t => selection.has(t.fdi)).length

  // Render a row of teeth
  const renderRow = (teeth: ToothData[], isUpper: boolean) => (
    <div className={clsx('flex justify-center', isUpper ? 'items-end' : 'items-start')}>
      {teeth.map(tooth => (
        <ToothCell
          key={tooth.id}
          tooth={tooth}
          isSelected={isSelected(tooth)}
          onSelect={() => toggleTooth(tooth)}
          displayNumber={tooth.fdi}
          isUpper={isUpper}
          size={toothSize}
        />
      ))}
    </div>
  )

  return (
    <div className="space-y-3">
      {/* Toolbar - FDI notation label and action buttons */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="px-3 py-2 text-xs font-bold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg">
            FDI Notation (11-48)
          </span>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={selectAll}
            className="px-3 py-2 text-xs font-bold text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 rounded-lg transition-colors">
            Select All
          </button>
          <button type="button" onClick={clearAll}
            className="px-3 py-2 text-xs font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Clear
          </button>
        </div>
      </div>

      {/* ── FULL ARCH VIEW (Desktop / iPad) ── */}
      {!isMobile && (
        <div className="relative bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 px-4 py-3 shadow-sm">

          {/* Quadrant labels */}
          <div className="flex justify-between items-center mb-1 px-1">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Upper Right (Q1: 11-18)</span>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Upper Left (Q2: 21-28)</span>
          </div>

          {/* Upper jaw */}
          <div className="flex justify-center items-end">
            <div className="flex items-end">
              {renderRow(UPPER_RIGHT, true)}
            </div>

            {/* Midline */}
            <div className="flex flex-col items-center mx-0.5 self-stretch justify-center">
              <div className="w-px h-full bg-gray-300 dark:bg-gray-600" style={{ minHeight: 40 }} />
            </div>

            <div className="flex items-end">
              {renderRow(UPPER_LEFT, true)}
            </div>
          </div>

          {/* Horizontal divider - jaw separator */}
          <div className="flex items-center my-1.5">
            <span className="text-[9px] font-bold text-gray-400 mr-2">R</span>
            <div className="flex-1 border-t-2 border-dashed border-gray-300 dark:border-gray-600" />
            <span className="text-[9px] font-bold text-gray-400 ml-2">L</span>
          </div>

          {/* Lower jaw */}
          <div className="flex justify-center items-start">
            <div className="flex items-start">
              {renderRow(LOWER_RIGHT, false)}
            </div>

            {/* Midline */}
            <div className="flex flex-col items-center mx-0.5 self-stretch justify-center">
              <div className="w-px h-full bg-gray-300 dark:bg-gray-600" style={{ minHeight: 40 }} />
            </div>

            <div className="flex items-start">
              {renderRow(LOWER_LEFT, false)}
            </div>
          </div>

          {/* Lower quadrant labels */}
          <div className="flex justify-between items-center mt-1 px-1">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Lower Right (Q4: 41-48)</span>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Lower Left (Q3: 31-38)</span>
          </div>
        </div>
      )}

      {/* ── QUADRANT VIEW (Mobile) ── */}
      {isMobile && (
        <div className="space-y-3">
          {/* Quadrant selector tabs */}
          <div className="grid grid-cols-4 gap-2">
            {(['UR', 'UL', 'LR', 'LL'] as ActiveQuadrant[]).map(q => {
              const m = QUADRANT_META[q]
              const cnt = qCount(q)
              const isActive = activeQuadrant === q
              return (
                <button key={q} type="button" onClick={() => setActiveQuadrant(q)}
                  className={clsx(
                    'relative flex flex-col items-center py-2 px-1 rounded-xl border-2 transition-all',
                    isActive
                      ? 'border-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 shadow-md'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                  )}>
                  <span className={clsx(
                    'text-sm font-bold',
                    isActive ? 'text-cyan-600' : 'text-gray-600 dark:text-gray-400'
                  )}>{m.qNum}</span>
                  <span className={clsx(
                    'text-[9px] leading-tight mt-0.5',
                    isActive ? 'text-cyan-500' : 'text-gray-400'
                  )}>{m.label}</span>
                  {cnt > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-cyan-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                      {cnt}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {/* Active quadrant display */}
          <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
            <div className="grid grid-cols-[auto_1fr_auto] items-start gap-2 mb-2">
              <div className="flex flex-col">
                <span className={clsx(
                  'text-xs font-bold uppercase tracking-wide',
                  QUADRANT_META[activeQuadrant].isUpper ? 'text-rose-500' : 'text-sky-500'
                )}>
                  {QUADRANT_META[activeQuadrant].label}
                </span>
                <span className="text-[11px] text-gray-400 leading-tight">
                  ({qCount(activeQuadrant)}/8 selected)
                </span>
              </div>
              <div />
              <button type="button" onClick={() => selectQuadrant(activeQuadrant)}
                className="text-xs font-bold text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 px-2.5 py-1.5 rounded-lg transition-colors">
                {qCount(activeQuadrant) === 8 ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div className="w-full pb-1">
              <div className={clsx('grid grid-cols-8 w-full gap-0.5 justify-items-center', QUADRANT_META[activeQuadrant].isUpper ? 'items-end' : 'items-start')}>
                {QUADRANT_META[activeQuadrant].teeth.map(tooth => (
                  <ToothCell
                    key={tooth.id}
                    tooth={tooth}
                    isSelected={isSelected(tooth)}
                    onSelect={() => toggleTooth(tooth)}
                    displayNumber={tooth.fdi}
                    isUpper={QUADRANT_META[activeQuadrant].isUpper}
                    size={toothSize}
                    compact
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selected teeth tags */}
      {displaySelection.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl border border-cyan-100 dark:border-cyan-800">
          <span className="text-xs font-bold text-cyan-700 dark:text-cyan-300 mr-1">
            {displaySelection.length} selected:
          </span>
          {displaySelection.map(toothNum => (
            <span key={toothNum}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white dark:bg-gray-800 border border-cyan-200 dark:border-cyan-700 text-cyan-700 dark:text-cyan-300 text-xs font-bold shadow-sm">
              {toothNum}
              <button type="button" onClick={() => { const td = findToothByFDI(toothNum); if (td) toggleTooth(td) }}
                className="ml-0.5 hover:bg-cyan-100 dark:hover:bg-cyan-700 rounded p-0.5 transition-colors">
                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                  <path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-md bg-gradient-to-br from-white to-gray-200 border border-gray-300 shadow-sm" />
          <span className="text-gray-500 font-medium">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-md bg-gradient-to-br from-cyan-200 to-cyan-400 border border-cyan-400 shadow-sm flex items-center justify-center">
            <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
              <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="text-gray-500 font-medium">Selected</span>
        </div>
      </div>
    </div>
  )
}
